/**
 * Minor Adjustments — shared threshold, formatting and boilerplate.
 *
 * Each covered payapp (G702/draw) has its own CDCC adjustment, and any payapp whose adjustment
 * exceeds the threshold gets its own labelled table. The section and the export must agree on which
 * payapps qualify and how the amount reads, so that logic lives here rather than in either one.
 *
 * The amounts are NOT stored in section fields. They are derived from each payapp's
 * `budgetSummary.adjustmentsByCDCC`, which is live data — persisting a copy would go stale the
 * moment the budget changed. Only the threshold is persisted.
 *
 * Kept free of component imports so it can be unit-tested directly.
 */

/** One payapp's CDCC adjustment. */
export interface DrawAdjustment {
  drawId: string;
  name: string;
  amount: number;
}

export const DEFAULT_MINOR_ADJUSTMENTS_THRESHOLD = 50;

export const MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT = `We have made a greater than normal Adjustment to the Contractor's request for funding due to several errors in the Contractor's G702/3 Form or for spreadsheet formula discrepancies we cannot account for.  We contacted the Contractor to inquire about the errors, but by the time of the writing of this report, we have not received a return call.  Since the amounts are not significant enough to warrant any delay in the delivery of our report, we have made the adjustment's below.`;

/** Plural form used when the report covers more than one payapp. */
export const MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT_MULTI = `We have made a greater than normal Adjustment to the Contractors' request for funding due to several errors in the Contractors' G702/3 Forms or for spreadsheet formula discrepancies we cannot account for.  We contacted the Contractors to inquire about the errors, but by the time of the writing of this report, we have not received a return call.  Since the amounts are not significant enough to warrant any delay in the delivery of our report, we have made the adjustments' below.`;

export const MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT = `In order to have our G702 Review Form and the Contractor's request for funding match exactly, we have made accommodations for small input errors or minor differences between our G702 Review Form and the amount requested from the Contractor.  These amounts are typically only a few dollars.`;

/** All boilerplate variants, so a stale auto-filled value can be recognised and replaced. */
export const MINOR_ADJUSTMENTS_BOILERPLATE_TEXTS = [
  MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT,
  MINOR_ADJUSTMENTS_ABOVE_THRESHOLD_TEXT_MULTI,
  MINOR_ADJUSTMENTS_WITHIN_THRESHOLD_TEXT,
];

/** A blank or unparseable stored threshold falls back to the default rather than to NaN. */
export const resolveMinorAdjustmentsThreshold = (raw?: string): number => {
  if (raw === undefined || raw === null || raw === '') return DEFAULT_MINOR_ADJUSTMENTS_THRESHOLD;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? DEFAULT_MINOR_ADJUSTMENTS_THRESHOLD : parsed;
};

/**
 * The payapps that get their own labelled table.
 *
 * Judged per payapp on the ABSOLUTE amount, so a large negative adjustment qualifies too. Applies
 * with a single payapp as well — the named table was previously missing in that case.
 */
export const filterAdjustmentsAboveThreshold = (draws: DrawAdjustment[], threshold: number): DrawAdjustment[] =>
  draws.filter(draw => Math.abs(draw.amount) > threshold);

/** "$191,481.80" / "$(37,853.87)" — negatives print in parentheses, per the report's convention. */
export const formatSignedCurrency = (amount: number): string => {
  const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return amount < 0 ? `$(${formatted})` : `$${formatted}`;
};
